import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { GlobeIcon, GraduationCapIcon, HandshakeIcon, MailIcon, MicIcon, PresentationIcon, QrCodeIcon, Share2Icon, UsersIcon } from 'lucide-react';
import { Section } from '../Section';
import { InView } from '../InView';
import { SectionHeadline } from '../SectionHeadline';
import { SnakStill } from '../../SnakStill';
import { ImageWindow } from '../ImageWindow';
import { shareChannels, snakkerSupport } from '../../../data/deck';
import type { ChannelId } from '../../../data/deck';
const CHANNEL_ICONS: Record<ChannelId, typeof GlobeIcon> = {
  email: MailIcon,
  website: GlobeIcon,
  symposium: MicIcon,
  training: GraduationCapIcon,
  qr: QrCodeIcon,
  congress: UsersIcon,
  iva: PresentationIcon,
  booth: HandshakeIcon
};
const BOOTH_IMAGE = 'https://cdn.magicpatterns.com/uploads/2vRYduetKkXLgu7bqng6U4/Screenshot_2026-10-22_at_11.49.41.png';
const BOOTH_SNAK_IMAGE = 'https://cdn.magicpatterns.com/uploads/xxT8CFkggCGDdDRAFgtzE5/Screenshot_2026-10-22_at_11.44.07.png';
export function ChannelsSection() {
  const [boothOpen, setBoothOpen] = useState(false);
  return <Section id="channels" data-id="element-383" data-mp="1899bwhdumilp">
      <AnimatePresence data-id="element-384" data-mp="ucdn9gl46ikj">
        {boothOpen && <ImageWindow key="booth" src={BOOTH_IMAGE} alt="Booth activation: a Snak screen on a phone beside the matching congress booth it was designed for" title="Booth / F2F · example booth activation" onClose={() => setBoothOpen(false)} data-id="element-385" data-mp="ubkqn8fvd9g4" />}
      </AnimatePresence>
      <div className="grid items-end gap-8 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-16" data-id="element-386" data-mp="aodab5361tze">
        <InView data-id="element-387" data-mp="fmojddln00gk">
          <SectionHeadline lead="One experience." follow="Many entry points." data-id="element-388" data-mp="fm6fbusqghy3" />
        </InView>
        <InView delay={0.1} data-id="element-389" data-mp="1mnabav7xmq0">
          <p className="text-[14.5px] leading-[1.7] text-[var(--muted)]" data-id="element-390" data-mp="1ohwesi87kvo">
            A single Snak can meet fertility specialists wherever attention already exists — without rebuilding the experience
            for every channel.
          </p>
        </InView>
      </div>

      <div className="mt-14 grid gap-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:gap-10" data-id="element-391" data-mp="23kgw6thw6and">
        <InView delay={0.15} y={26} data-id="element-392" data-mp="1ykum2sxd1ais">
          <figure className="flex h-full flex-col rounded-[30px] bg-[var(--fg)] p-6 sm:p-7" data-id="element-393" data-mp="1ylvxiyiafz11">
            <div className="flex items-center gap-4" data-id="element-394" data-mp="1ullofcz78shy">
              <span className="flex h-12 w-12 items-center justify-center rounded-[14px] bg-white/10 text-[var(--brand-lighter)]" data-id="element-395" data-mp="2ratd2tv47hq3">
                <GlobeIcon className="h-5 w-5" aria-hidden="true" data-id="element-396" data-mp="2mci3mk8qf98e" />
              </span>
              <div data-id="element-397" data-mp="2i8k78haskcj1">
                <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--brand-lighter)]" data-id="element-398" data-mp="2i822sxdz6s01">
                  One build
                </span>
                <span className="mt-1 block text-[22px] font-extrabold leading-none tracking-[-0.02em] text-white" data-id="element-399" data-mp="16uq1at5oulh1">
                  The REKOVELLE Snak
                </span>
              </div>
            </div>
            <div className="mt-6 flex flex-1 items-center justify-center rounded-[22px] bg-[var(--surface)] px-6 py-8" data-id="element-400" data-mp="3dk04smevdbwf">
              <SnakStill screen={0} alt="The REKOVELLE teaser Snak, shared across every channel" className="h-[min(56vh,520px)] min-h-[360px] w-[min(29vh,270px)] min-w-[186px]" data-id="element-401" data-mp="3ii1mb2wnd35s" />
            </div>
          </figure>
        </InView>

        <div className="flex flex-col" data-id="element-402" data-mp="3ap27b4t2ga">
          <InView delay={0.2} data-id="element-403" data-mp="52n7mfuyznd2">
            <div className="flex items-end justify-between gap-6" data-id="element-404" data-mp="51u9u4dzo872">
              <div data-id="element-405" data-mp="a29lmrimh7mx">
                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--muted)]" data-id="element-406" data-mp="a1qfs968y9p9">The same Snak</span>
                <h3 className="mt-2 text-[28px] font-extrabold leading-[1.08] tracking-[-0.03em] text-[var(--fg)] sm:text-[32px]" data-id="element-407" data-mp="e2a7uybyqn4j">
                  Ready for every relevant touchpoint
                </h3>
              </div>
              <span className="hidden shrink-0 rounded-full bg-[var(--brand)] px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-white sm:inline-flex" data-id="element-408" data-mp="2fx16664m9tvw">
                One build · many entries
              </span>
            </div>
          </InView>

          <ul className="mt-7 grid flex-1 grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-409" data-mp="1uhmvdwma7z1g">
            {shareChannels.map((channel, i) => {
            const Icon = CHANNEL_ICONS[channel.id];
            const body = <>
                  <span className="flex h-11 w-11 items-center justify-center rounded-[12px] bg-[var(--brand)]/10 text-[var(--brand)]" data-id="element-410" data-mp="11s7fasfrffyo">
                    <Icon className="h-5 w-5" aria-hidden="true" data-id="element-411" data-mp="1bklou7d2xqve" />
                  </span>
                  <span className="mt-6" data-id="element-412" data-mp="16tn14mprhqi4">
                    <span className="block text-[16px] font-extrabold leading-tight tracking-[-0.02em] text-[var(--fg)]" data-id="element-413" data-mp="16rsjbh9seei9">
                      {channel.name}
                    </span>
                    <span className="mt-1 block text-[12px] leading-snug text-[var(--muted)]" data-id="element-414" data-mp="e3wo6gzv8unl">{channel.detail}</span>
                  </span>
                </>;
            return <InView key={channel.id} delay={0.24 + i * 0.04} className="h-full" data-id="element-415" data-mp="2ujt826ol8fmq" data-mpi={i}>
                  <li className="h-full" data-id="element-416" data-mp="2gs64dlsbg4tx">
                    {channel.id === 'booth' ? <button type="button" onClick={() => setBoothOpen(true)} aria-label="Booth / F2F — view an example booth" className="group relative flex h-full min-h-[170px] w-full flex-col justify-between overflow-hidden rounded-[20px] bg-white p-3 text-left ring-1 ring-[var(--fg)]/[0.08] transition-shadow hover:ring-2 hover:ring-[var(--brand)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]" data-id="element-417" data-mp="2ks6rrvxe7qd4">
                        <span className="block h-[110px] overflow-hidden rounded-[12px] bg-[var(--surface)] ring-1 ring-[var(--fg)]/[0.06]" data-id="element-418" data-mp="1o4sdo5f7o5i9">
                          <img src={BOOTH_SNAK_IMAGE} alt="Booth Snak screen: '5 OCT variables. One clustering model.' with retinal imaging" className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.04]" data-id="element-419" data-mp="3urhkdo2yb66r" />
                        </span>
                        <span className="mt-3 block px-2 pb-1" data-id="element-420" data-mp="3c070hqvn7tod">
                          <span className="block text-[16px] font-extrabold leading-tight tracking-[-0.02em] text-[var(--fg)]" data-id="element-421" data-mp="3bydnzuycmf8g">
                            {channel.name}
                          </span>
                          <span className="mt-1 block text-[12px] leading-snug text-[var(--muted)]" data-id="element-422" data-mp="324dfdj3uyruz">{channel.detail}</span>
                        </span>
                        <span className="absolute right-4 top-4 rounded-full bg-[var(--brand)] px-2.5 py-1 text-[9.5px] font-bold uppercase tracking-[0.14em] text-white" data-id="element-423" data-mp="109w8m3bwe0bf">
                          View
                        </span>
                      </button> : <div className="flex h-full min-h-[170px] flex-col justify-between rounded-[20px] bg-white p-5 ring-1 ring-[var(--fg)]/[0.08]" data-id="element-424" data-mp="2j58tuygw5r1u">
                        {body}
                      </div>}
                  </li>
                </InView>;
          })}
          </ul>

          <InView delay={0.5} data-id="element-425" data-mp="1v9eis6adr27r">
            <p className="mt-3 flex items-center gap-4 rounded-[20px] bg-white px-5 py-5 text-[17px] font-bold leading-snug text-[var(--fg)] ring-1 ring-[var(--fg)]/[0.08] sm:text-[19px]" data-id="element-426" data-mp="1va65sspwvrmf">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--brand)] text-white" data-id="element-427" data-mp="247gfwaqztapr">
                <Share2Icon className="h-4 w-4" aria-hidden="true" data-id="element-428" data-mp="3fnyby9qohbbv" />
              </span>
              Consistent experience, consistent measurement — regardless of where the journey begins.
            </p>
          </InView>
        </div>
      </div>

      <InView delay={0.2} data-id="element-429" data-mp="2wb91nphk9rtp">
        <div className="mt-14 grid gap-8 border-t border-[var(--fg)]/10 pt-10 lg:grid-cols-2 lg:items-end lg:gap-16" data-id="element-430" data-mp="2w8d2xyxw2z47">
          <p className="max-w-[26ch] text-[28px] font-extrabold leading-[1.08] tracking-[-0.03em] text-[var(--fg)] sm:text-[34px]" data-id="element-431" data-mp="352rfvuuy5xk5">
            Creative consistency at regional level.{' '}
            <span className="text-[var(--brand)]" data-id="element-432" data-mp="30a7z6p0yqmb4">Relevant activation at local level.</span>
          </p>
          <div data-id="element-433" data-mp="3a1ulbgeua5sn">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--muted)]" data-id="element-434" data-mp="3a248yxcew0v6">
              Snakker supports local brand teams with
            </span>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-435" data-mp="3nzatoxgp49n1">
              {snakkerSupport.map(item => <span key={item} className="rounded-full border border-[var(--fg)]/20 px-3 py-1 text-[12px] font-medium text-[var(--fg)]" data-id="element-436" data-mp="3jy79jyrm7rg0">
                  {item}
                </span>)}
            </div>
            <p className="mt-3 text-[12px] text-[var(--muted)]" data-id="element-437" data-mp="1fivpef9zhhf8">Practical support — not a new platform or an extra layer.</p>
          </div>
        </div>
      </InView>
    </Section>;
}